import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSearchParams, useNavigate } from "react-router-dom";
import LoggedLayout from '../../layouts/LoggedLayout';
import styled from 'styled-components';
import PageHeader from 'components/PageHeader';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import DateDropdown from 'components/DateDropdown';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { default as ClockingsService } from 'services/Clockings';
import { default as StationsService } from 'services/Stations';
import { hasCapability, getUser } from 'helpers/user';
import TableAlert from './TableAlert';
import GpsPopup from './GpsPopup';

const Filters = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;

	.filters-inputs {
	    display: flex;
		flex-direction: row;
		align-items: center;
		flex-wrap: nowrap;

		@media (max-width: 1200px) {
			& {
				display: flex !important;
				justify-content: flex-end;
				width: 100% !important;
			}
		}

		@media (min-width: 576px) and (max-width: 1200px) {
			& > *:nth-child(2) {
				min-width: 200px;
			}
		}

		@media (min-width: 768px) and (max-width: 991px) {
			#search-box {
				flex: 1;
			}
		}

		@media (max-width: 576px) {
			flex-direction: column;
			align-items: stretch;
			gap: 1rem;
			width: 100%;
			max-width: 100%;
			box-sizing: border-box;

			& > * {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}

			#search-box {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}
		}

		@media (max-width: 374px) {
			& {
				margin: auto;
			}
		}
	}

	.filter-buttons{
		width: auto !important;

		& > * {
			@media (min-width: 1201px) {
				height: 100%;
			}

			@media (max-width: 576px) {
				width: 100%;
				text-align: center;
			}
		}

		@media (max-width: 1200px) {
			margin-right: 0.5rem;
		}

		@media (max-width: 991px) {
			width: calc(100% - 1.5rem) !important;
		}

		@media (max-width: 767px) {
			width: calc(100% - 2rem);
		}

		@media (max-width: 576px) {
			display: flex !important;
			flex-direction: column !important;
			flex-wrap: nowrap;
			align-items: stretch !important;
			width: 100% !important;
		}
	}
	
	@media (max-width: 1200px) {
		justify-content: center;
		flex-direction: column;
		align-items: flex-end;

		& > *,
		.empowered-selector {
			width: 100% !important;
			display: block !important;
		}

		& > * {
			margin-bottom: 10px;
		}
	}

	@media (max-width: 991px) {
		display: grid;
		grid-template-columns: 1fr;
		grid-auto-rows: auto;
		gap: 10px;
		align-items: stretch;

		& > * {
			width: 100% !important;
			margin-bottom: 0 !important;
		}
	}

	@media (max-width: 767px) {
		grid-template-columns: 1fr;
	}
`;

const DateDropDownWrapper = styled.div` 
	padding: 0;
	min-height: 0;
	width: 192px;
	display: inline-block;

	& > .date-dropdown {
		display: block;

		& > .dropdown {
			width: 100%;
			display: block !important;

			& > .btn {
				padding: 0;
				width: 100% !important;
				display: flex;
				align-items: center;
				padding: 0 5px;

				small {
					margin-left: auto;
				}
			}
		}
	}
`;


const EmpoweredSelectorWrapper = styled.div` 
	margin-right: 10px;

	.empowered-selector {
		width: 200px;
		
		.empowered-selector_label {
			height: 28px;
			white-space: nowrap;
			overflow: hidden;

			&:hover {
				background: white;
			}

			.remove-icon {
				background: white;
				border-radius: 50%;
			}
		}
	}
`;

export default function Dispensers(props) {
	const [queryParams] = useSearchParams();
	const navigate = useNavigate();

	const responsible_id = queryParams.get('responsible_id');

	let [loading, setLoading] = useState(true);
	let [dispensers, setDispensers] = useState([]);
	let [delaysCount, setDelaysCount] = useState(0);
	let [noRecordsCount, setNoRecordsCount] = useState(0);
	let [responsibles, setResponsibles] = useState([]);
	let [date, setDate] = useState(moment().format('YYYY-MM-DD'));
	let [station, setStation] = useState(null);
	let [responsible, setResponsible] = useState({
		id: responsible_id,
		name: null
	});
	let [dispenser, setDispenser] = useState(null);
	let [sortDirection, setSortDirection] = useState('asc');
	let [sortField, setSortField] = useState('name');
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [onlyAlerts, setOnlyAlerts] = useState(false);

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

	const loadStations = async (input, callback) => {
		let result = await StationsService.getList({
			search: input,
			no_paginate: true
		});

		let formatted = result.map((el) => {
			return {
				value: el, 
				label: el.name
			};
		});

		callback(formatted);
	}

	const loadResponsibles = async (input, callback, args) => {
		let result = await ClockingsService.getResponsibles({
			no_paginate: true,
			search: input,
			station_id: args.station_id
		});

		let formatted = result.map((el) => {
			return {
				value: el, 
				label: el.name
			};
		});

		callback(formatted);
	}

	const loadDispensers = async (input, callback, args) => {
		let result = await ClockingsService.getDispensers({
			search: input,
			// station_id: args.stationId
		});

		let formatted = result.map((el) => {
			return {
				value: el, 
				label: el.first_name + ' ' + el.last_name
			};
		});

		callback(formatted);
	}

	useEffect(() => {
		// Get responsibles only if clocking primary
		if ( hasCapability('clockings_primary') ) {
			let getResponsibles = async () => {
				let responsibles = await ClockingsService.getResponsibles({
					no_paginate: true,
					sort: 'name',
					direction: 'asc',
					station_id: station?.id,
				});
				setResponsibles(responsibles);
			}
			getResponsibles();
		} else { // Clocking secondary
			setResponsibles([getUser()]);
		}

		// Get dispensers
		let getDispensersSchedules = async () => {
			setLoading(true);
			setDispensers([]);
			let dispensers = await ClockingsService.getDispensersSchedules({
				sort: sortField,
				direction: sortDirection,
				date: date,
				station_id: station?.id,
				employee_id: dispenser?.id
			});
			setLoading(false);
			setSkeletonRows(dispensers?.length >= 5 ? dispensers.length : 5);
			setDispensers(dispensers);

			if ( dispensers ) {
				let delays = 0;
				let noRecords = 0;
				dispensers.forEach(d => {
					d.intervals.forEach(i => {
						if ( i.alerts.isLateEnter ) {
							delays++;
						}
						if ( i.alerts.notEntered ) {
							noRecords++;
						}
					});
				});
				setDelaysCount(delays);
				setNoRecordsCount(noRecords);
			}
		}
		getDispensersSchedules();
	}, [sortField, sortDirection, station?.id, dispenser?.id, date]);

	useEffect(() => {
		if ( responsible_id ) {
			let loadResponsible = async () => {
				let responsible = await ClockingsService.getResponsible(responsible_id);
				setResponsible(responsible);
			}
			loadResponsible();
		}
	}, [responsible_id]);

	useEffect(() => {
		// Fix to hide empty responsibles wrapper
		const elements = document.querySelectorAll('[id^="responsible-wrapper-"]');
		elements.forEach((el) => {
			if (el.querySelector('tbody tr')) {
				const rowCount = el.querySelectorAll('tbody tr:not(.empty-tr)').length;
				if (rowCount === 0) {
					el.style.display = 'none';
				} else {
					el.style.display = 'block';
				}
			}
		});
	}, [dispensers, responsible, dispenser, onlyAlerts]);

	const toggleAlertForRevision = async (e, dispenserData, intervalIndex) => {
		e.preventDefault();
		e.stopPropagation();

		let dispenserIdx = dispensers.findIndex(d => d.employee_id === dispenserData.employee_id && d.date === dispenserData.date && d.schedule_id === dispenserData.schedule_id);
		if ( dispenserIdx === -1 ) return;

		const alert = dispenserData['intervals'][intervalIndex].alert_for_revision;
		
		const result = await ClockingsService.updateAlertStatus(alert.id, alert.checked_at ? false : true);
		if (result.status) {
			let newDispensers = [...dispensers];
			newDispensers[dispenserIdx]['intervals'][intervalIndex].alert_for_revision.checked_at = alert.checked_at ? false : true;
			setDispensers(newDispensers);
		}
	}

	return (
		<LoggedLayout>
			<div className="col-md-12 mb-3">
				<PageHeader>
					<span className="header-column header-title">Control de turnos</span>

					<span className="header-column">
						<div className="header-column-text d-flex flex-row flex-nowrap align-items-center gap-2">
							TARDANZAS&nbsp;
						</div>
						<div className="header-column-number" style={{color: 'var(--plenoil-red)'}}>
							{delaysCount ?? '-'}
						</div>
					</span>

					<span className="header-column">
						<div className="header-column-text d-flex flex-row flex-nowrap align-items-center gap-2">
							NO REGISTRADAS&nbsp;
						</div>
						<div className="header-column-number" style={{color: 'var(--plenoil-red)'}}>
							{noRecordsCount ?? '-'}
						</div>
					</span>

					<span className="header-column header-icon-end">
						<i className="bi bi-file-spreadsheet text-plenoil-gray"></i>
					</span>
				</PageHeader>
			</div>

			<div className="col-md-12 mb-3">
				<div className="bg-white p-2 rounded shadow-sm">
					<div className="row">
						<Filters className="col-lg-10 gap-1 gap-lg-2 filters-inputs">
							<DateDropDownWrapper className="form-control form-control_flotas control-input me-2 h-100">
								<DateDropdown 
									defaultPeriod="custom"
									onChange={(period, range) => setDate(range.from)}
									onlyDates={true}
									mode="single"
									showPeriods={false}
									startRange={{from: date, to: date}}
								/>
							</DateDropDownWrapper>
							<EmpoweredSelectorWrapper>
								<EmpoweredSelector
									load={(input, callback) => loadStations(input, callback)}
									onChange={(value, text) => setStation(value)}
									timeout={200}
									label={station?.name}
									value={station?.id ?? null}
									placeholder="Estación"
								/>
							</EmpoweredSelectorWrapper>
							{ hasCapability('clockings_primary') &&
									<EmpoweredSelectorWrapper>
										<EmpoweredSelector
											load={(input, callback, args) => loadResponsibles(input, callback, args)}
											args={{station_id: station?.id}}
											onChange={(value, text) => setResponsible(value)}
											timeout={200}
											label={responsible?.name}
											value={responsible?.id ?? null}
											placeholder="Responsable"
										/>
									</EmpoweredSelectorWrapper>
							}
							<EmpoweredSelectorWrapper>
								<EmpoweredSelector
									load={(input, callback, args) => loadDispensers(input, callback, args)}
									args={{stationId: station?.id}}
									onChange={(value, text) => setDispenser(value)}
									timeout={200}
									label={dispenser ? dispenser.first_name + ' ' + dispenser.last_name : ''}
									value={dispenser?.id ?? null}
									placeholder="Expendedor"
								/>
							</EmpoweredSelectorWrapper>
							<div className="col-md-2">
								<div className="form-check form-switch d-flex align-items-center gap-1 flex-nowrap">
									<input className="form-check-input" type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} />
									<label className="form-check-label">Sólo mostrar alertas</label>
								</div>
							</div>
						</Filters>
					</div>
				</div>
			</div>

			<div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								{ responsibles.filter(el => {
									if ( hasCapability('clockings_primary') && responsible?.id ) {
										return el.id === responsible.id;
									}
									return true;
								}).map((rEl, rIdx) => {
									let showEmpty = true;

									return (
										<div className="mb-5" key={'r' + rIdx} id={'responsible-wrapper-' + rEl.id}>
											<h2>{rEl.name}</h2>

											<div className="table-responsive-rows">
												<div className="mvc-grid mvc-grid-header-mode">
													<div className="mvc-grid-table">
														<table>
															<thead>
																<tr>
																	<th className="title_fichajes-dia">Día</th>
																	<ThSortable className="title_fichajes-expendedor" direction={sortDirection} active={sortField === 'name'} onClick={() => sortTableClick('name')}>Expendedor</ThSortable>
																	<ThSortable className="title_fichajes-estacion" direction={sortDirection} active={sortField === 'station_name'} onClick={() => sortTableClick('station_name')}>Estación</ThSortable>
																	<ThSortable className="title_fichajes-turno" direction={sortDirection} active={sortField === 'schedule_name'} onClick={() => sortTableClick('schedule_name')}>Turno</ThSortable>
																	<th className="title_fichajes-horario">Horario esperado</th>
																	<th className="title_fichajes-entrada">Entrada</th>
																	<th className="title_fichajes-salida">Salida</th>
																	<th className="title_fichajes-entrada">Entrada</th>
																	<th className="title_fichajes-salida">Salida</th>
																	<th></th>
																</tr>
															</thead>
															<tbody>
																{ dispensers?.filter(el => {
																	// If capability clockings_primary then show only the responsible's dispensers
																	if ( hasCapability('clockings_primary') ) {
																		return el.head_employees?.find(he => he.id === rEl.id) ? true : false;
																	}
																	return true;
																}).filter(el => {
																	if (onlyAlerts) {
																		let hasAlerts = false;
																		el.intervals.forEach(i => {
																			if (i.with_alert) {
																				hasAlerts = true;
																				return;
																			}
																		});
																		if (hasAlerts) {
																			return true;
																		} else {
																			return false;
																		}
																	}

																	return true;
																}).map(el => {
																	showEmpty = false;

																	return (
																		<React.Fragment key={'r' + rEl.id + '-e' + el.employee_id}>
																			<tr onClick={() => navigate('/clockings/dispenser/' + el.employee_id + '?schedule_day=' + el.date)}>
																				<td className="title_fichajes-dia">{moment(el.date).format('DD-MM-YYYY')}</td>
																				<td className="title_fichajes-expendedor">{el.employee_name}</td>
																				<td className="title_fichajes-estacion">{el.station_name}</td>
																				<td className="title_fichajes-turno">{el.schedule_name}</td>
																				<td className="title_fichajes-horario">
																					{el.intervals &&
																						el.intervals.map((i, iIdx) => {
																							return (
																								<div key={'r' + rEl.id + '-e' + el.id + '-in' + iIdx}>{moment('2024-11-07 ' + i.start_at).format('HH:mm')} / {moment('2024-11-07 ' + i.end_at).format('HH:mm')}</div>
																							);
																						})
																					}
																				</td>
																				<td className="position-relative title_fichajes-entrada">
																					<TableAlert interval={el.intervals[0]} what={'start_at'} />

																					{el.intervals[0]?.alert_for_revision &&
																						<div className="alert-for-revision__wrapper" onClick={(e) => toggleAlertForRevision(e, el, 0)}>
																							<i className={"bi bi-eye" + (el.intervals[0].alert_for_revision.checked_at ? '-fill text-primary' : '')}></i>
																						</div>
																					}
																				</td>
																				<td className="title_fichajes-salida"><TableAlert interval={el.intervals[0]} what={'end_at'} /></td>

																				<td className="position-relative title_fichajes-entrada">
																					<TableAlert interval={el.intervals[1]} what={'start_at'} />

																					{el.intervals[1]?.alert_for_revision &&
																						<div className="alert-for-revision__wrapper" onClick={(e) => toggleAlertForRevision(e, el, 1)}>
																							<i className={"bi bi-eye" + (el.intervals[1].alert_for_revision.checked_at ? '-fill text-primary' : '')}></i>
																						</div>
																					}
																				</td>
																				<td className="title_fichajes-salida"><TableAlert interval={el.intervals[1]} what={'end_at'} /></td>
																				<td>
																					<GpsPopup 
																						employeeId={el.employee_id}
																						date={el.date}
																					/>
																				</td>
																			</tr>
																		</React.Fragment>
																	)
																}) }

																{ (showEmpty && !loading) && <tr className="empty-tr"><td colSpan="100%">No hay datos disponibles</td></tr> }

																{ loading && <TrSkeleton rows={skeletonRows} columns={10} /> }
															</tbody>
														</table>
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoggedLayout>
	);
}